import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import {markRaw} from "vue"
import * as _ from "lodash"
import {Am5Settings} from "@/mixins/am5charts/Am5Settings"
import {Am5SeriesColor} from "@/mixins/am5charts/Am5SeriesColor";
import {Am5SeriesDataLoader} from "@/mixins/am5charts/Am5SeriesDataLoader"

const COLOR_LEFT = am5.color("#3d81f7")
const COLOR_RIGHT = am5.color("#1c4019")

const REPLACE = 0
const PREPEND = 1
const APPEND = 2

export const Am5Series = class {
    constructor(root, company, chart, id, sideAxis, scalingProfile, start = null, end = null, axisPosition = Am5Settings.AXIS_LEFT, loadBackFillIntervals = false) {
        // This object owns live am5 Sprites, so it must never become reactive.
        // Vue's reactive() proxies deeply, which would give every am5 object
        // underneath us a second identity. am5 detaches a child's canvas display
        // with `$array.removeFirst(container._children, child._display)` — a ===
        // match — so a Sprite added under one identity and removed under the other
        // is never spliced out of its parent's display list and keeps painting as
        // a stale, offset "ghost" copy of the series. markRaw here makes that
        // impossible no matter which component stores us.
        markRaw(this)
        this.create(root, company, chart, id, sideAxis, scalingProfile, start, end, axisPosition, loadBackFillIntervals)
    }

    setupSeries(shouldShow = true) {
        if (!this.isSetupValid()) {
            return
        }

        if (!this.isAm5SeriesReady()) {
            this.setupAm5Series()
        }

        if (shouldShow && !this.visible) {
            this.show()

            let color = Am5SeriesColor.nextColor()
            this.color = color
            this.am5Series.set("stroke", color)
            this.am5Series.set("fill", color)
        } else if (!this.visible) {
            this.hide()
        }

        let seriesList = this.seriesChart.series
        if (seriesList.indexOf(this.am5Series) !== -1) {
            return
        }

        this.seriesChart.series.push(this.am5Series)
        this.setupBullets()
        this.setupTooltip()

        this.setAxis()
    }

    getAm5Series() {
        return this.am5Series
    }

    getSeries() {
        return this.am5Series
    }

    getYAxis() {
        if (_.isNil(this.sideAxis)) {
            return
        }

        return this.sideAxis.axis
    }

    setAxisLeft() {
        this.axisPosition = Am5Settings.AXIS_LEFT
        this.color = COLOR_LEFT
        this.setAxis()
    }

    setAxisRight() {
        this.axisPosition = Am5Settings.AXIS_RIGHT
        this.color = COLOR_RIGHT
        this.setAxis()
    }

    setAxis() {
        if (_.isNil(this.sideAxis)) {
            console.error(`Am5Series@setAxis - Side Axis is not set. Series: ${this.name}(${this.id})`)
            return
        }

        this.am5Series.set("yAxis", this.seriesChart.yAxes.getIndex(this.sideAxis.axisIndex))

        this.am5Series.set("stroke", this.color)
        this.am5Series.set("fill", this.color)

        this.setupThresholds()
    }

    setSideAxis(axis) {
        this.sideAxis = axis
    }

    setDateAxis(axis) {
        this.dateAxis = axis
    }

    removeSeries() {
        if (!this.isSetupValid()) {
            return
        }

        const am5SeriesRef = this.am5Series
        this.hide()
        this.reset()
        this.seriesChart.series.removeValue(am5SeriesRef)
    }

    hide() {
        if (!this.isSetupValid()) {
            return
        }

        if (this.isAm5SeriesReady()) {
            this.am5Series.hide()
        }
        this.setThresholdsVisible(false)
        this.visible = false
    }

    show() {
        if (!this.isSetupValid()) {
            return
        }

        this.am5Series.show()
        this.visible = true
        this.setThresholdsVisible(this.thresholdsEnabled)

        this.seriesDataLoader.start()
    }

    isVisible() {
        return this.visible
    }

    reset() {
        this.backFillIntervals = []

        // Destroy this series' threshold ranges before dispose() nulls this.sideAxis.
        this.destroyThresholds()

        if (this.isAm5SeriesReady()) {
            this.am5Series.data.clear()
            this.am5Series.bullets.clear()
            this.dispose()
        }

        this.sideAxis = null
    }

    setupAm5Series() {
        this.dispose()

        let xAxis = this.seriesChart.xAxes.getIndex(0)

        if (this.scalingProfile && this.scalingProfile.has_text_labels_on_graph) {
            this.am5Series = am5xy.StepLineSeries.new(this.root, {
                xAxis: xAxis,
                yAxis: this.sideAxis.getAxis(),
                valueYField: 'value',
                valueXField: 'timestamp',
                connect: true,
            })
        } else {
            this.am5Series = am5xy.LineSeries.new(this.root, {
                xAxis: xAxis,
                yAxis: this.sideAxis.getAxis(),
                valueYField: 'value',
                valueXField: 'timestamp',
                connect: false,
                groupData: false,
            })
        }

        this.am5Series.strokes.template.setAll({
            strokeWidth: 2,
        })

        this.am5Series.set("stroke", this.color)
        this.am5Series.set("fill", this.color)
    }

    setupBullets() {
        if (!this.isSetupValid()) {
            return
        }

        if (_.isNil(this.am5Series)) {
            return
        }

        this.am5Series.bullets.clear()
        this.am5Series.bullets.push((root, series, dataItem) => {
            let dataContext = dataItem.dataContext
            let bgColor = dataContext && dataContext.backgroundColor
                ? (typeof dataContext.backgroundColor === 'string' ? am5.color(dataContext.backgroundColor) : dataContext.backgroundColor)
                : this.color

            let circle = am5.Circle.new(root, {
                radius: 3,
                fill: bgColor,
                stroke: bgColor,
            })

            // At render time, hide this bullet if its dataItem is not in the
            // currently active dataset. This handles the oscillation between raw
            // and grouped datasets where both get bullet factory calls.
            circle.adapters.add("visible", (visible, target) => {
                let di = target.dataItem
                if (!di) return visible
                let activeItems = series._dataItems
                return activeItems.indexOf(di) !== -1 ? visible : false
            })

            return am5.Bullet.new(root, {
                sprite: circle,
            })
        })
    }

    setupTooltip() {
        if (!this.isSetupValid()) {
            return
        }

        let tooltip = am5.Tooltip.new(this.root, {
            pointerOrientation: "horizontal",
            getFillFromSprite: false,
            getLabelFillFromSprite: true,
            labelText: "{valueY}",
        })

        tooltip.get("background").setAll({
            cornerRadius: 20,
            fillOpacity: 1,
        })

        tooltip.label.setAll({
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 12,
            paddingRight: 12,
        })

        // Adapter on the tooltip label to replace resolved template text
        // with our custom tooltip content from the data point's dataContext.
        // Uses target._dataItem per amcharts5 issue #115.
        //
        // For grouped data: when am5 groups data, series.dataItems contains
        // grouped DataItems (with _groupedStats set by groupDataCallback).
        // We find the closest grouped DataItem by binary-searching series.dataItems.
        let self = this
        tooltip.label.adapters.add("text", (text, target) => {
            let di = target._dataItem
            if (!di) return text

            if (di.dataContext && di.dataContext.tooltipContent) {
                return di.dataContext.tooltipContent
            }

            return text
        })

        // Set tooltip background color from data, falling back to series color
        let series = this.am5Series
        tooltip.get("background").adapters.add("fill", (fill, target) => {
            let dataItem = series.get("tooltipDataItem")
            if (dataItem && dataItem.dataContext && dataItem.dataContext.backgroundColor) {
                let bgColor = dataItem.dataContext.backgroundColor
                return typeof bgColor === 'string' ? am5.color(bgColor) : bgColor
            }
            return self.color
        })

        tooltip.get("background").adapters.add("stroke", (stroke, target) => {
            let dataItem = series.get("tooltipDataItem")
            if (dataItem && dataItem.dataContext && dataItem.dataContext.backgroundColor) {
                let bgColor = dataItem.dataContext.backgroundColor
                return typeof bgColor === 'string' ? am5.color(bgColor) : bgColor
            }
            return self.color
        })

        this.am5Series.set("tooltip", tooltip)
    }

    setupThresholds() {
        let valueAxis = this.getYAxis()
        if (_.isNil(valueAxis)) {
            return
        }

        // Build the ranges once per owning axis and toggle their visibility from
        // then on. Removing and recreating axis ranges on every series
        // add/remove races with am5's pending layout pass and throws
        // "EventDispatcher is disposed" from inside _renderFrame; that aborted
        // frame strands the already-drawn line graphics on the canvas, which is
        // what surfaces as a duplicated, offset copy of the series.
        if (this.thresholdAxis !== valueAxis) {
            this.destroyThresholds()
            this.thresholdAxis = valueAxis

            let alarmInfo = this.getAlarmInfo()
            let alarmValues = {}
            this.setupThresholdRange(alarmInfo.alarm, Am5Settings.COLOR_ALARM, alarmValues)
            this.setupThresholdRange(alarmInfo.caution, Am5Settings.COLOR_CAUTION, alarmValues)
        }

        this.thresholdsEnabled = true
        this.setThresholdsVisible(this.visible)

        return this.alarmThresholds
    }

    clearThresholds() {
        if (!this.isSetupValid()) {
            return
        }

        this.thresholdsEnabled = false
        this.setThresholdsVisible(false)
    }

    setThresholdsVisible(visible) {
        _.each(this.am5ThresholdRanges, range => {
            if (_.isNil(range)) {
                return
            }

            let grid = range.get("grid")
            if (grid) {
                grid.set("visible", visible)
            }
        })
    }

    destroyThresholds() {
        // Only remove this series's own threshold ranges; the side-axis may also
        // own labeled ranges on the same axisRanges list (text-label profiles).
        if (!_.isNil(this.thresholdAxis) && !this.thresholdAxis.isDisposed()) {
            _.each(this.am5ThresholdRanges, range => {
                this.thresholdAxis.axisRanges.removeValue(range)
            })
        }

        this.am5ThresholdRanges = []
        this.thresholdAxis = null
    }

    setupThresholdRange(values, color, alarmValues) {
        // Create against the axis destroyThresholds() will remove them from, so
        // the two can never drift onto different axes.
        let valueAxis = this.thresholdAxis

        if (_.isNil(valueAxis) || _.isNil(values) || typeof values !== 'object') {
            return
        }

        Object.keys(values).forEach(key => {
            let item = values[key]
            if (item === null || item === undefined) {
                return
            }

            let dataItem = valueAxis.makeDataItem({ value: item })
            let range = valueAxis.createAxisRange(dataItem)
            let grid = range.get("grid")
            if (_.isNil(grid)) {
                return
            }
            grid.setAll({
                stroke: color,
                strokeWidth: 2,
                strokeOpacity: 1,
                visible: true,
            })

            if (this.axisPosition === Am5Settings.AXIS_RIGHT) {
                range.get("grid").set("strokeDasharray", [5, 4])
            }

            this.am5ThresholdRanges.push(range)

            if (key === 'high_alarm') {
                alarmValues.highAlarmValue = item;
            } else if (key === 'low_alarm') {
                alarmValues.lowAlarmValue = item;
            } else if (key === 'high_caution') {
                alarmValues.highCautionValue = item;
            } else if (key === 'low_caution') {
                alarmValues.lowCautionValue = item;
            }
        })

        this.alarmThresholds = alarmValues
    }

    updateFromSeriesData(seriesData) {
        Object.keys(seriesData).forEach(key => {
            this[key] = seriesData[key]
        })
    }

    setPointData(data, addMode = REPLACE) {
        if (_.isNil(data)) {
            return
        }

        if (!this.isAm5SeriesReady()) {
            return
        }

        if (addMode === REPLACE) {
            this.am5Series.data.clear()
            this.backFillIntervals = []
        }

        data = this.processPointData(data, addMode)
        let firstReading = data[0]

        this.seriesDataLoader.logLoadedInterval(firstReading.date, data[data.length - 1].date)

        // Find where to insert the data
        let existingData = this.am5Series.data.values
        let indexToPlaceAt = existingData.length
        for (let index = 0; index < existingData.length; index++) {
            let testDate = existingData[index].date
            if (testDate.getTime() > firstReading.date.getTime()) {
                indexToPlaceAt = index
                break
            }
        }

        let first = _.slice(existingData, 0, indexToPlaceAt)
        let last = _.slice(existingData, indexToPlaceAt, existingData.length)
        let tmp = _.concat(first, data, last)

        // Check for duplicate timestamps with different values before dedup
        let seen = {}
        tmp.forEach(item => {
            if (seen[item.timestamp] !== undefined && seen[item.timestamp] !== item.value) {
                console.warn(`[DUPE] id=${this.id} ts=${new Date(item.timestamp).toISOString()} v1=${seen[item.timestamp]} v2=${item.value}`)
            }
            seen[item.timestamp] = item.value
        })

        // Deduplicate by timestamp
        tmp = _.uniqBy(tmp, item => item.timestamp)

        this.am5Series.data.setAll(tmp)

        this.updateMinMax(tmp)

        // Only update axis intervals when all data batches have loaded,
        // not during each batch. This avoids redundant axis recalculations
        // (the am4 equivalent had a cascading regrouping bug here).
        if (!this.isLoadingData()) {
            this.dateAxis.updateDateAxisInterval()
        }
        this.dateAxis.updateGapAndMinBulletDistance()

        this.updateRender()
    }

    updateMinMax(data) {
        this.min = _.minBy(data, 'value').value
        this.max = _.maxBy(data, 'value').value

        // Every threshold has to stay inside the axis range, not only the ones on
        // the side of the data they are named for. The side axis renders with
        // strictMinMax, so a threshold outside [min, max] draws its grid line off
        // the plot area and the point looks like it has no thresholds at all --
        // ex. a profile with only high thresholds (45 degF) on a point reading
        // around 70 degF.
        _.each(this.getThresholdValues(), value => {
            this.min = _.min([this.min, value])
            this.max = _.max([this.max, value])
        })

        if (this.scalingProfile && this.scalingProfile.force_min_max && this.scalingProfile.force_min_max.length === 2) {
            let forceMin = this.scalingProfile.force_min_max[0]
            let forceMax = this.scalingProfile.force_min_max[1]
            if (forceMin !== null && forceMin !== undefined && forceMin !== '') {
                this.min = _.min([this.min, forceMin])
            }
            if (forceMax !== null && forceMax !== undefined && forceMax !== '') {
                this.max = _.max([this.max, forceMax])
            }
        }

        this.sideAxis.updateMinMax()
    }

    processPointData(data, addMode = REPLACE) {
        let backFillStart = false
        _.forEach(data, (item, index) => {
            item.color = this.color
            item.backgroundColor = this.color
            item.date = new Date(item.date)
            item.timestamp = item.date.getTime()
            // Rename "tooltip" to "tooltipContent" to avoid collision with
            // am5's reserved "tooltip" property on DataItem
            item.tooltipContent = item.tooltip
            delete item.tooltip
            let timestamp = item.timestamp
            if (this.validationReadingsMap[timestamp]) {
                item.backgroundColor = '#ff0000'

                if (item.tooltipContent) {
                    const validationReading = this.validationReadingsMap[timestamp]
                    let userInfo = ''
                    if (validationReading.user) {
                        const displayName = validationReading.user.display_name || 'Unknown User'
                        const email = validationReading.user.email || 'No email'
                        userInfo = `\nInserted by: ${displayName} (${email})`
                    }
                    item.tooltipContent = item.tooltipContent + `\n\nValidation Alarm Reading\nThis data point was inserted via a Validation Alarm test.${userInfo}`
                }
            }

            if (backFillStart && !item.isBackfilled) {
                this.backFillIntervals.push({
                    start: _.clone(backFillStart),
                    end: _.clone(item.date),
                })
                backFillStart = false
            } else if (!backFillStart && item.isBackfilled) {
                backFillStart = _.clone(item.date)
            }

            data[index] = item
        })

        if (backFillStart) {
            this.backFillIntervals.push({
                start: _.clone(backFillStart),
                end: _.clone(data[data.length - 1].date),
            })
            backFillStart = false
        }

        // A trailing null value is the server's "page complete" sentinel. Its absence
        // means the page was truncated at the server's load limit, so we must request
        // the remainder no matter which addMode delivered this page — a truncated
        // PREPEND/APPEND (e.g. from a date-range change) would otherwise silently
        // drop everything past the limit. Overlap with already-loaded data is fine;
        // setPointData dedupes by timestamp.
        let lastReading = data[data.length - 1]
        if (lastReading.value === null) {
            data.pop()
        } else {
            let start = new Date(lastReading.date.getTime() + 1)
            this.seriesDataLoader.addInterval(start, this.end, APPEND)
        }

        return this.injectGapNulls(data)
    }

    injectGapNulls(data) {
        if (!Am5Settings.useShortGaps) {
            return data
        }

        const gapMs = Am5Settings.SHORT_GAP_LENGTH * 60 * 1000
        const result = []
        for (let i = 0; i < data.length; i++) {
            result.push(data[i])
            if (i < data.length - 1) {
                let diff = data[i + 1].timestamp - data[i].timestamp
                if (diff > gapMs) {
                    let nullTs = data[i].timestamp + 1
                    result.push({
                        date: new Date(nullTs),
                        timestamp: nullTs,
                        value: null,
                        color: this.color,
                        backgroundColor: this.color,
                        tooltipContent: '',
                    })
                }
            }
        }
        return result
    }

    getFirstReadingDate() {
        if (this.numData() === 0) {
            return
        }

        let reading = this.am5Series.data.values[0]
        return _.clone(reading.date)
    }

    getLastReadingDate() {
        if (this.numData() === 0) {
            return
        }

        let reading = this.am5Series.data.values[this.numData() - 1]
        return _.clone(reading.date)
    }

    loadData(start = null, end = null) {
        this.seriesDataLoader.addInterval(start, end)
    }

    isLoadingData() {
        return this.seriesDataLoader.intervalsToLoad.length > 0
    }

    numData() {
        return _.isNil(this.am5Series) || _.isNil(this.am5Series.data) ? 0 : this.am5Series.data.values.length
    }

    updateInterval(start, end) {
        let oldStart = _.clone(this.start)
        let oldEnd = _.clone(this.end)
        this.start = _.clone(start)
        this.end = _.clone(end)
        if (!this.isAm5SeriesReady()) {
            return
        }

        let startChanged = oldStart.getTime() !== this.start.getTime()
        let endChanged = oldEnd.getTime() !== this.end.getTime()
        if (!startChanged && !endChanged) {
            return
        }

        if (!this.isVisible()) {
            this.am5Series.data.clear()
            this.backFillIntervals = []
            this.seriesDataLoader.reset()
            return
        }

        this.seriesDataLoader.clearQueue()

        let newStart = this.start.getTime()
        let newEnd = this.end.getTime()
        let oldStartTime = oldStart.getTime()
        let oldEndTime = oldEnd.getTime()

        if (newStart > oldStartTime) {
            this.removeDataBetween(oldStart, this.start)
        }

        if (newEnd < oldEndTime) {
            this.removeDataBetween(this.end, oldEnd)
        }

        if (newStart < oldStartTime) {
            this.seriesDataLoader.addInterval(this.start, oldStart, PREPEND)
        }

        if (newEnd > oldEndTime) {
            this.seriesDataLoader.addInterval(oldEnd, this.end, APPEND)
        }

        this.updateBackFillIntervals()
    }

    removeDataBetween(start, end) {
        let data = this.am5Series.data.values

        let startTime = start.getTime()
        let startIndex = _.findIndex(data, item => item.date.getTime() >= startTime)
        // No point falls at or after the start of the removal window, so the
        // window (e.g. a new end that sits past the last loaded reading) covers
        // no data — keep everything. Without this guard findIndex's -1 turns
        // into startIndex 0 and the slice below wipes the entire series,
        // surfacing as a spurious "No data found in specified range".
        if (startIndex === -1) {
            return
        }
        startIndex = _.max([0, startIndex - 1])

        let endTime = end.getTime()
        let endIndex = _.findIndex(data, item => item.date.getTime() > endTime)
        endIndex = endIndex === -1
            ? data.length
            : _.min([data.length, endIndex])

        let newData = _.concat(_.slice(data, 0, startIndex), _.slice(data, endIndex))
        this.am5Series.data.setAll(newData)

        if (!_.isNil(Am5Settings.updateVueHook)) {
            Am5Settings.updateVueHook()
        }
    }

    updateRender() {
        if (this.loadBackFillIntervals) {
            this.renderBackFillIntervals()
        }
    }

    updateBackFillIntervals() {
        this.backFillIntervals = _.filter(this.backFillIntervals, interval => {
            return interval.start.getTime() < this.end.getTime() && interval.end.getTime() > this.start.getTime()
        })
    }

    renderBackFillIntervals(scrollbar = null) {
        if (!_.isNil(scrollbar)) {
            let sbChart = scrollbar.chart
            if (sbChart && sbChart.xAxes.length > 0) {
                this.scrollAxis = sbChart.xAxes.getIndex(0)
            }
        } else {
            this.scrollAxis = this.dateAxis.getScrollAxisIfExists()
        }

        if (_.isNil(this.scrollAxis)) {
            return
        }

        if (!this.loadBackFillIntervals) {
            this.clearBackFillIntervals()
            return
        }

        this.scrollAxis.axisRanges.clear()
        _.forEach(this.backFillIntervals, interval => {
            let dataItem = this.scrollAxis.makeDataItem({
                value: interval.start.getTime(),
                endValue: interval.end.getTime(),
            })
            let range = this.scrollAxis.createAxisRange(dataItem)
            range.get("axisFill").setAll({
                fillOpacity: 0.3,
                fill: Am5Settings.BACK_FILL_COLOR,
                visible: true,
            })
        })
    }

    clearBackFillIntervals() {
        if (!_.isNil(this.scrollAxis)) {
            this.scrollAxis.axisRanges.clear()
        }
    }

    getBackFillIntervals() {
        return this.backFillIntervals
    }

    hasBackFillIntervals() {
        return this.backFillIntervals.length > 0
    }

    isSetupValid() {
        return !_.isNil(this.seriesChart)
    }

    setAlarmInfo(alarmInfo) {
        this.alarmInfo = alarmInfo
    }

    setValidationReadings(readings) {
        this.validationReadingsMap = {}
        if (!Array.isArray(readings)) {
            return
        }
        readings.forEach(r => {
            if (r.time) {
                let timestamp = new Date(r.time).getTime()
                this.validationReadingsMap[timestamp] = r
            }
        })
    }

    getAlarmInfo() {
        if (_.isNil(this.alarmInfo)) {
            return {
                alarm: {},
                caution: {},
            }
        }

        return this.alarmInfo
    }

    getThresholdValues() {
        return _.filter([
            this.alarmThresholds.lowAlarmValue,
            this.alarmThresholds.lowCautionValue,
            this.alarmThresholds.highCautionValue,
            this.alarmThresholds.highAlarmValue,
        ], value => !_.isNil(value))
    }

    isAm5SeriesReady() {
        return !_.isNil(this.am5Series) && !this.am5Series.isDisposed()
    }

    updateLoadingBackFillIntervals(loadBackFillIntervals) {
        this.loadBackFillIntervals = loadBackFillIntervals
    }

    create(root, company, chart, id, sideAxis, scalingProfile, start = null, end = null, axisPosition = Am5Settings.AXIS_LEFT, loadBackFillIntervals = false) {
        this.root = root
        this.requestNumber = 0
        this.company = company
        this.min = 100000
        this.max = -100000
        this.lastUpdateTime = null
        this.alarmInfo = null
        this.start = _.isNil(start) ? new Date() : _.clone(start)
        this.end = _.isNil(end) ? new Date() : _.clone(end)
        if (_.isNil(start)) {
            this.start.setDate(this.end.getDate() - 1)
        }
        this.scalingProfile = scalingProfile
        this.am5ThresholdRanges = []
        this.alarmThresholds = {}
        this.thresholdAxis = null
        this.thresholdsEnabled = true

        this.visible = false
        this.seriesChart = chart
        this.color = Am5SeriesColor.DEFAULT_COLOR

        this.id = id
        this.axisPosition = axisPosition
        this.sideAxis = sideAxis
        this.dateAxis = null
        this.scrollAxis = null
        this.seriesDataLoader = new Am5SeriesDataLoader(this)
        this.backFillIntervals = []
        this.loadBackFillIntervals = loadBackFillIntervals
        this.validationReadingsMap = {}

        this.am5Series = null
    }

    dispose() {
        if (!_.isNil(this.am5Series) && !this.am5Series.isDisposed()) {
            this.am5Series.dispose()
            this.am5Series = null
            this.sideAxis = null

            Am5SeriesColor.releaseColor(this.color)
        }
        this.seriesDataLoader.stop()
    }
}
